import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { AgentAvatar, shrinkToAvatar } from './AgentAvatar'
import { useToast } from './ui/Toaster'

/**
 * Trocar a foto de alguém.
 *
 * Serve aos dois casos com o mesmo componente, porque são a mesma operação: a pessoa mudando a
 * própria e um admin mudando a de outro. Quem pode é decidido no servidor — a rota aceita se for
 * o próprio ou se for admin — e não por esconder o botão, que é decoração e não autorização.
 *
 * A imagem é reduzida aqui antes de subir. O arquivo que sai de um celular tem megabytes e o
 * destino é um círculo de 22 pixels.
 */
export function AvatarPicker({
  userId,
  name,
  color,
  avatarUrl,
  size = 40,
  onDone,
}: {
  userId: string
  name: string
  color?: string | null
  avatarUrl?: string | null
  size?: number
  onDone?: () => void
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  const save = useMutation({
    mutationFn: (dataUrl: string | null) => api.setAvatar(userId, dataUrl),
    onSuccess: (_d, dataUrl) => {
      // Toda tela que desenha gente: a caixa, a conversa, a lista de usuários e o próprio menu.
      qc.invalidateQueries()
      toast(dataUrl ? 'Foto atualizada.' : 'Foto removida.')
      onDone?.()
    },
    onError: (e: Error) => toast(e.message || 'Não foi possível salvar a foto.'),
  })

  async function pick(file: File | undefined) {
    if (!file) return
    setBusy(true)
    try {
      save.mutate(await shrinkToAvatar(file))
    } catch {
      // Um arquivo que o navegador não consegue decodificar não é uma imagem, por mais que a
      // extensão diga que é.
      toast('Não consegui ler esse arquivo como imagem.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="avp">
      <AgentAvatar name={name} color={color} avatarUrl={avatarUrl} size={size} />
      <div className="avp-actions">
        <button
          className="btn sec sm"
          disabled={busy || save.isPending}
          onClick={() => input.current?.click()}
        >
          {busy || save.isPending ? 'Enviando…' : avatarUrl ? 'Trocar foto' : 'Enviar foto'}
        </button>
        {avatarUrl && (
          // A única forma de desfazer: sem isto, uma foto ruim fica para sempre.
          <button className="linkish" disabled={save.isPending} onClick={() => save.mutate(null)}>
            remover
          </button>
        )}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          void pick(e.target.files?.[0])
          // Zerado para que escolher o MESMO arquivo de novo dispare o evento outra vez.
          e.target.value = ''
        }}
      />
    </div>
  )
}
